import { useState, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import type { LinkRelaySourceView, LinkReportState, LinkStatusView } from '@reform-lite/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useT } from '@/i18n/use-t';
import { useConnectorLocationSettings } from '@/hooks/use-connector-location-settings';
import {
  useLinkStatus,
  useRequestLinkRelayResend,
  useRetryLinkExport,
} from '@/hooks/use-link-status';
import {
  useMasterkasaExports,
  useMasterkasaSources,
  useRetryMasterkasaExport,
  type MasterkasaExportRow,
  type MasterkasaSource,
} from '@/hooks/use-masterkasa';
import { CorrectionAction, CorrectionList } from './export-correction';

/**
 * RL-453 — per store, what has not reached the store's own system and why,
 * for BOTH hosts an export can run on:
 *
 * - Back Office: each connector source's outbox, read live from
 *   `/connectors/sources/:id/exports`, retried on the spot;
 * - Link: what the store's Link last reported. Back Office cannot reach a
 *   Link, so a retry is a request the Link collects on its next report — the
 *   panel shows it as waiting until the Link answers.
 *
 * A Link that has not reported recently is shown as such and its queue is
 * not shown at all: an old count must never read as the current one.
 *
 * RL-554: with the Link owning a store's catalogue, it also relays the store
 * system's cashiers and promotions to Back Office; the Link section shows,
 * per source, when it last did, what Back Office made of it, and lets an
 * operator ask for everything again.
 */
export function ExportStatusPanel({ canEdit }: { canEdit: boolean }): JSX.Element {
  const t = useT('admin_ui');
  const stores = useConnectorLocationSettings(true);
  const sources = useMasterkasaSources(true);
  const [picked, setPicked] = useState<string | null>(null);
  const rows = stores.data ?? [];
  const locationId = picked ?? rows[0]?.locationId ?? null;
  const store = rows.find((row) => row.locationId === locationId) ?? null;
  const storeSources = (sources.data ?? []).filter((s) => s.location.id === locationId);

  return (
    <div className="space-y-4 border-t border-border pt-5">
      <div>
        <h3 className="text-sm font-semibold">{t('settings.integrations.exportStatus.title')}</h3>
        <p className="max-w-3xl text-xs text-muted-foreground">
          {t('settings.integrations.exportStatus.description')}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('settings.integrations.ownership.empty')}
        </p>
      ) : (
        <>
          <div className="flex max-w-md items-center gap-2">
            <Label htmlFor="export-status-store">
              {t('settings.integrations.ownership.store')}
            </Label>
            <Select
              id="export-status-store"
              value={locationId ?? ''}
              onChange={(event) => setPicked(event.target.value)}
            >
              {rows.map((row) => (
                <option key={row.locationId} value={row.locationId}>
                  {row.location.name} ({row.location.code})
                </option>
              ))}
            </Select>
          </div>
          {store !== null && (
            <p className="text-xs text-muted-foreground">
              {t('settings.integrations.ownership.exportOwner')}:{' '}
              <span className="font-medium text-foreground">
                {t(`settings.integrations.ownership.owner.${store.exportOwner}`)}
              </span>
            </p>
          )}
          <BackofficeExports
            sources={storeSources}
            canRetry={canEdit && store?.exportOwner === 'backoffice'}
          />
          {locationId !== null && <LinkExports locationId={locationId} canEdit={canEdit} />}
        </>
      )}
    </div>
  );
}

function errorText(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as { message?: string } | undefined;
    return body?.message ?? fallback;
  }
  return fallback;
}

function when(value: string | null): string {
  if (value === null) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

// ---------------------------------------------------------------- Back Office

function BackofficeExports({
  sources,
  canRetry,
}: {
  sources: MasterkasaSource[];
  canRetry: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  return (
    <section aria-label={t('settings.integrations.exportStatus.backoffice')} className="space-y-2">
      <h4 className="text-sm font-medium">{t('settings.integrations.exportStatus.backoffice')}</h4>
      {sources.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('settings.integrations.exportStatus.noSources')}
        </p>
      ) : (
        sources.map((source) => (
          <SourceExports key={source.id} source={source} canRetry={canRetry} />
        ))
      )}
    </section>
  );
}

function SourceExports({
  source,
  canRetry,
}: {
  source: MasterkasaSource;
  canRetry: boolean;
}): JSX.Element {
  const t = useT('admin_ui');
  const exports = useMasterkasaExports(source.id, true);
  const retry = useRetryMasterkasaExport();
  const [error, setError] = useState<string | null>(null);
  const counts = exports.data?.counts ?? {};
  const open = (exports.data?.items ?? []).filter((row) => row.status !== 'written');

  async function retryRow(row: MasterkasaExportRow): Promise<void> {
    setError(null);
    try {
      await retry.mutateAsync({ sourceId: source.id, exportId: row.id });
    } catch (cause) {
      setError(errorText(cause, t('settings.integrations.exportStatus.retryError')));
    }
  }

  return (
    <div className="space-y-1 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{source.name}</span>
        <QueueCounts
          pending={counts.pending ?? 0}
          failed={counts.failed ?? 0}
          conflict={counts.conflict ?? 0}
        />
      </div>
      {exports.isError ? (
        <p className="text-xs text-destructive">
          {t('settings.integrations.exportStatus.loadError')}
        </p>
      ) : open.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('settings.integrations.ownership.queueEmpty')}
        </p>
      ) : (
        <ProblemTable
          rows={open.map((row) => ({
            id: row.id,
            kind: row.kind,
            documentId: row.documentId,
            status: row.status,
            attempts: row.attempts,
            reason: row.lastError,
            at: row.documentAt,
          }))}
          retryable={(row) => canRetry && (row.status === 'failed' || row.status === 'conflict')}
          onRetry={(row) => void retryRow(open.find((r) => r.id === row.id)!)}
          pending={retry.isPending}
        />
      )}
      {error !== null && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Link

const STATE_VARIANT: Record<LinkReportState, 'success' | 'destructive' | 'outline'> = {
  no_link: 'outline',
  missing: 'destructive',
  stale: 'destructive',
  ok: 'success',
  degraded: 'destructive',
  failing: 'destructive',
};

function LinkExports({ locationId, canEdit }: { locationId: string; canEdit: boolean }) {
  const t = useT('admin_ui');
  const query = useLinkStatus(locationId);
  const retry = useRetryLinkExport();
  const [error, setError] = useState<string | null>(null);
  const view: LinkStatusView | undefined = query.data;

  async function retryRow(exportId: string): Promise<void> {
    setError(null);
    try {
      await retry.mutateAsync({ locationId, exportId });
    } catch (cause) {
      setError(errorText(cause, t('settings.integrations.exportStatus.retryError')));
    }
  }

  return (
    <section aria-label={t('settings.integrations.exportStatus.link')} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-medium">{t('settings.integrations.exportStatus.link')}</h4>
        {view !== undefined && (
          <Badge variant={STATE_VARIANT[view.state]}>
            {t(`settings.integrations.exportStatus.state.${view.state}`)}
          </Badge>
        )}
      </div>
      {query.isError ? (
        <p className="text-xs text-destructive">
          {t('settings.integrations.exportStatus.loadError')}
        </p>
      ) : view === undefined ? (
        <p className="text-xs text-muted-foreground">
          {t('settings.integrations.ownership.loading')}
        </p>
      ) : view.device === null ? (
        <p className="text-xs text-muted-foreground">
          {t('settings.integrations.exportStatus.noLink')}
        </p>
      ) : (
        <div className="space-y-2 rounded-md border border-border p-3 text-sm">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">
              {t('settings.integrations.exportStatus.device')}
            </dt>
            <dd>
              {view.device.displayName} ({view.device.code})
            </dd>
            <dt className="text-muted-foreground">
              {t('settings.integrations.exportStatus.reportedAt')}
            </dt>
            <dd>{when(view.report?.receivedAt ?? null)}</dd>
            {view.report !== null && (
              <>
                <dt className="text-muted-foreground">
                  {t('settings.integrations.exportStatus.version')}
                </dt>
                <dd className="font-mono">{view.report.version}</dd>
                <dt className="text-muted-foreground">
                  {t('settings.integrations.exportStatus.address')}
                </dt>
                <dd className="font-mono">{view.report.advertisedUrl ?? '—'}</dd>
                {view.report.certificateFingerprints.length > 0 && (
                  <>
                    <dt className="text-muted-foreground">
                      {t('settings.integrations.exportStatus.fingerprints')}
                    </dt>
                    <dd className="break-all font-mono">
                      {view.report.certificateFingerprints.join(', ')}
                    </dd>
                  </>
                )}
              </>
            )}
            {/* VL-378: the Link's gift-certificate mode — always disabled in this build; no action. */}
            {view.giftCertificates ? (
              <>
                <dt className="text-muted-foreground">
                  {t('settings.integrations.exportStatus.giftCertificates')}
                </dt>
                <dd data-testid="link-gift-certificates">
                  {t('settings.integrations.exportStatus.giftCertificatesDisabled')}
                </dd>
              </>
            ) : null}
          </dl>

          {view.report !== null && view.report.sources.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>{t('settings.integrations.masterkasa.source')}</TH>
                  <TH>{t('settings.integrations.masterkasa.status')}</TH>
                  <TH>{t('settings.integrations.masterkasa.cursor')}</TH>
                  <TH>{t('settings.integrations.exportStatus.faults')}</TH>
                  <TH>{t('settings.integrations.exportStatus.lastSuccess')}</TH>
                </TR>
              </THead>
              <TBody>
                {view.report.sources.map((source) => (
                  <TR key={source.sourceId}>
                    <TD>{source.name ?? source.sourceId}</TD>
                    <TD>
                      {t(`settings.integrations.exportStatus.import.${source.import.state}`)}
                      {source.import.lastError !== null && (
                        <div
                          role={source.import.state === 'ok' ? 'status' : undefined}
                          className={
                            source.import.state === 'ok'
                              ? 'text-xs text-amber-700 dark:text-amber-400'
                              : 'text-xs text-destructive'
                          }
                        >
                          {source.import.lastError}
                        </div>
                      )}
                    </TD>
                    <TD className="font-mono text-xs">{source.import.cursor ?? '—'}</TD>
                    <TD>{source.import.faults}</TD>
                    <TD>{when(source.import.lastSuccessAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {(view.relay ?? []).length > 0 && (
            <RelayTable
              locationId={locationId}
              rows={view.relay ?? []}
              canEdit={canEdit}
              onError={setError}
            />
          )}

          {view.exportQueue === null ? (
            <p className="text-xs text-muted-foreground">
              {t('settings.integrations.ownership.queueUnknown')}
            </p>
          ) : (
            <>
              <QueueCounts {...view.exportQueue.counts} />
              {view.exportQueue.problems.length > 0 && (
                <ProblemTable
                  rows={view.exportQueue.problems.map((problem) => ({
                    id: problem.id,
                    kind: problem.kind,
                    documentId: problem.documentId,
                    status: problem.status,
                    attempts: problem.attempts,
                    reason: problem.reason,
                    at: problem.documentAt,
                  }))}
                  retryable={() => canEdit && view.exportOwner === 'link'}
                  onRetry={(row) => void retryRow(row.id)}
                  pending={retry.isPending}
                  // VL-400: correcting an invalid add method, where offered.
                  extra={(row) => (
                    <CorrectionAction locationId={locationId} row={row} view={view} />
                  )}
                  requestState={(row) => {
                    const request = view.retryRequests.find((r) => r.exportId === row.id);
                    if (request === undefined) return null;
                    if (request.completedAt === null) {
                      return t(
                        request.deliveredAt === null
                          ? 'settings.integrations.exportStatus.retry.waiting'
                          : 'settings.integrations.exportStatus.retry.delivered',
                      );
                    }
                    return t(`settings.integrations.exportStatus.retry.${request.outcome ?? ''}`);
                  }}
                />
              )}
            </>
          )}
          <CorrectionList corrections={view.corrections ?? []} />
        </div>
      )}
      {error !== null && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </section>
  );
}

function RelayTable({
  locationId,
  rows,
  canEdit,
  onError,
}: {
  locationId: string;
  rows: LinkRelaySourceView[];
  canEdit: boolean;
  onError: (message: string | null) => void;
}): JSX.Element {
  const t = useT('admin_ui');
  const resend = useRequestLinkRelayResend();

  async function resendSource(sourceId: string): Promise<void> {
    onError(null);
    try {
      await resend.mutateAsync({ locationId, sourceId });
    } catch (cause) {
      onError(errorText(cause, t('settings.integrations.exportStatus.relay.resendError')));
    }
  }

  const count = (label: string, value: number, bad = false) => (
    <Badge variant={bad && value > 0 ? 'destructive' : 'outline'}>
      {t(`settings.integrations.exportStatus.relay.${label}`)}: {value}
    </Badge>
  );

  return (
    <section aria-label={t('settings.integrations.exportStatus.relay.title')} className="space-y-1">
      <h5 className="text-xs font-medium">{t('settings.integrations.exportStatus.relay.title')}</h5>
      <p className="text-xs text-muted-foreground">
        {t('settings.integrations.exportStatus.relay.description')}
      </p>
      <Table>
        <THead>
          <TR>
            <TH>{t('settings.integrations.masterkasa.source')}</TH>
            <TH>{t('settings.integrations.exportStatus.relay.lastRelayed')}</TH>
            <TH>{t('settings.integrations.exportStatus.relay.cashiers')}</TH>
            <TH>{t('settings.integrations.exportStatus.relay.promotions')}</TH>
            {canEdit && <TH className="text-right">{t('settings.integrations.actions')}</TH>}
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => (
            <TR key={row.sourceId}>
              <TD>
                <div>{row.name ?? row.sourceId}</div>
                {row.errors.map((text, index) => (
                  <div key={index} className="text-xs text-destructive">
                    {text}
                  </div>
                ))}
              </TD>
              <TD>
                <div>{when(row.lastRelayedAt)}</div>
                {row.fullInProgress && (
                  <Badge variant="outline">
                    {t('settings.integrations.exportStatus.relay.inProgress')}
                  </Badge>
                )}
                {row.resendRequested && (
                  <Badge variant="outline">
                    {t('settings.integrations.exportStatus.relay.resendRequested')}
                  </Badge>
                )}
              </TD>
              <TD>
                {row.cashiers === null ? (
                  '—'
                ) : (
                  <span className="flex flex-wrap gap-1 text-xs">
                    {count('created', row.cashiers.created)}
                    {count('updated', row.cashiers.updated)}
                    {count('suspended', row.cashiers.suspended)}
                    {count('failed', row.cashiers.failed, true)}
                  </span>
                )}
              </TD>
              <TD>
                {row.promotions === null ? (
                  '—'
                ) : (
                  <span className="flex flex-wrap gap-1 text-xs">
                    {count('mapped', row.promotions.mapped)}
                    {count('unmapped', row.promotions.unmapped)}
                  </span>
                )}
              </TD>
              {canEdit && (
                <TD className="text-right">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={resend.isPending || row.resendRequested}
                    aria-label={`${t('settings.integrations.exportStatus.relay.resend')} ${row.name ?? row.sourceId}`}
                    onClick={() => void resendSource(row.sourceId)}
                  >
                    {t('settings.integrations.exportStatus.relay.resend')}
                  </Button>
                </TD>
              )}
            </TR>
          ))}
        </TBody>
      </Table>
    </section>
  );
}

// ---------------------------------------------------------------- shared

function QueueCounts({
  pending,
  failed,
  conflict,
}: {
  pending: number;
  failed: number;
  conflict: number;
}): JSX.Element {
  const t = useT('admin_ui');
  return (
    <span className="flex flex-wrap gap-2 text-xs">
      <Badge variant="outline">
        {t('settings.integrations.exportStatus.status.pending')}: {pending}
      </Badge>
      <Badge variant={failed > 0 ? 'destructive' : 'outline'}>
        {t('settings.integrations.exportStatus.status.failed')}: {failed}
      </Badge>
      <Badge variant={conflict > 0 ? 'destructive' : 'outline'}>
        {t('settings.integrations.exportStatus.status.conflict')}: {conflict}
      </Badge>
    </span>
  );
}

type ProblemRow = {
  id: string;
  kind: string;
  documentId: string;
  status: string;
  attempts: number;
  reason: string | null;
  at: string | null;
};

function ProblemTable({
  rows,
  retryable,
  onRetry,
  pending,
  requestState,
  extra,
}: {
  rows: ProblemRow[];
  retryable: (row: ProblemRow) => boolean;
  onRetry: (row: ProblemRow) => void;
  pending: boolean;
  requestState?: (row: ProblemRow) => string | null;
  extra?: (row: ProblemRow) => ReactNode;
}): JSX.Element {
  const t = useT('admin_ui');
  return (
    <Table>
      <THead>
        <TR>
          <TH>{t('settings.integrations.exportStatus.document')}</TH>
          <TH>{t('settings.integrations.masterkasa.status')}</TH>
          <TH>{t('settings.integrations.exportStatus.reason')}</TH>
          <TH className="text-right">{t('settings.integrations.actions')}</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => {
          const state = requestState?.(row) ?? null;
          return (
            <TR key={row.id}>
              <TD>
                <div>{t(`settings.integrations.exportStatus.kind.${row.kind}`)}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {row.documentId} · {when(row.at)}
                </div>
              </TD>
              <TD>
                <Badge variant={row.status === 'pending' ? 'outline' : 'destructive'}>
                  {t(`settings.integrations.exportStatus.status.${row.status}`)}
                </Badge>
                <div className="text-xs text-muted-foreground">
                  {t('settings.integrations.exportStatus.attempts')}: {row.attempts}
                </div>
              </TD>
              <TD className="max-w-md text-xs">{row.reason ?? '—'}</TD>
              <TD className="text-right">
                {retryable(row) && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending}
                    aria-label={`${t('settings.integrations.exportStatus.retry')} ${row.documentId}`}
                    onClick={() => onRetry(row)}
                  >
                    {t('settings.integrations.exportStatus.retry')}
                  </Button>
                )}
                {state !== null && (
                  <div className="mt-1 text-xs text-muted-foreground">{state}</div>
                )}
                {extra?.(row)}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
